import React, { useState, useEffect } from 'react';
import './BhajanManagement.css';
import AddCategoryForm from './AddCategoryForm';
import AddBhajanForm from './AddBhajanForm';
import BhajanCategoryCard from './BhajanCategoryCard';
import API_CONFIG from '../../config/apiConfig';

const BhajanManagement = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
    const [showAddBhajanForm, setShowAddBhajanForm] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [bhajanSearchTerm, setBhajanSearchTerm] = useState('');

    // Edit states
    const [editingCategory, setEditingCategory] = useState(null);
    const [editCategoryName, setEditCategoryName] = useState('');

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const apiUrl = API_CONFIG.getBhajanCategoriesUrl();
            const response = await fetch(apiUrl);

            if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data)) {
                    setCategories(data);
                } else if (data && Array.isArray(data.results)) {
                    setCategories(data.results);
                } else {
                    setCategories([]);
                }
                setError(null);
            } else {
                const errorText = await response.text();
                setError(`API Error (${API_CONFIG.CURRENT_ENV}): ${response.status} - ${errorText}`);
                setCategories([]);
            }
        } catch (err) {
            setError(`Network Error (${API_CONFIG.CURRENT_ENV}): ${err.message}`);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategoryDetails = async (categoryId) => {
        try {
            const response = await fetch(`https://pooja-path-1.onrender.com/api/bhajan_sangrah/categories/${categoryId}/`);
            if (response.ok) {
                const data = await response.json();
                setSelectedCategory(data);
            }
        } catch (err) {
            console.error('Error fetching category details:', err);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm('Are you sure you want to delete this category?')) return;
        try {
            const response = await fetch(`https://pooja-path-1.onrender.com/api/bhajan_sangrah/categories/${categoryId}/delete/`, { method: 'DELETE' });
            if (response.ok) {
                alert('Category deleted successfully');
                await fetchCategories();
                if (selectedCategory?.id === categoryId) setSelectedCategory(null);
            } else {
                const errorData = await response.json();
                alert(`Failed to delete category: ${errorData.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert('Failed to delete category');
        }
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setEditCategoryName(category.name);
    };

    const handleCancelEditCategory = () => {
        setEditingCategory(null);
        setEditCategoryName('');
    };

    const handleSaveEditCategory = async () => {
        try {
            const response = await fetch(`https://pooja-path-1.onrender.com/api/bhajan_sangrah/categories/${editingCategory.id}/update/`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: editCategoryName })
            });
            if (response.ok) {
                alert('Category updated successfully');
                setEditingCategory(null);
                setEditCategoryName('');
                await fetchCategories();
                if (selectedCategory?.id === editingCategory.id) {
                    setSelectedCategory({ ...selectedCategory, name: editCategoryName });
                }
            } else {
                alert('Failed to update category');
            }
        } catch (err) {
            alert('Error updating category');
        }
    };

    const handleDeleteBhajan = async (categoryId, bhajanTitle) => {
        if (!window.confirm('Are you sure you want to delete this bhajan?')) return;
        try {
            const encodedTitle = encodeURIComponent(bhajanTitle);
            const response = await fetch(`https://pooja-path-1.onrender.com/api/bhajan_sangrah/categories/${categoryId}/bhajans/${encodedTitle}/delete/`, { method: 'DELETE' });
            if (response.ok) {
                alert('Bhajan deleted successfully');
                await fetchCategoryDetails(categoryId);
            } else {
                const errorData = await response.json();
                alert(`Failed to delete bhajan: ${errorData.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert('Failed to delete bhajan');
        }
    };

    const handleEditBhajan = async (categoryId, oldTitle, newTitle, newText) => {
        try {
            const encodedTitle = encodeURIComponent(oldTitle);
            const response = await fetch(`https://pooja-path-1.onrender.com/api/bhajan_sangrah/categories/${categoryId}/bhajans/${encodedTitle}/update/`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: newTitle, bhajan_text: newText })
            });
            if (response.ok) {
                alert('Bhajan updated successfully');
                await fetchCategoryDetails(categoryId);
            } else {
                alert('Failed to update bhajan');
            }
        } catch (err) {
            alert('Error updating bhajan');
        }
    };

    const handleCategoryAdded = () => { setShowAddCategoryForm(false); fetchCategories(); };
    const handleBhajanAdded = () => { setShowAddBhajanForm(false); selectedCategory && fetchCategoryDetails(selectedCategory.id); };

    const filteredCategories = categories.filter(category => category.name.toLowerCase().includes(categorySearchTerm.toLowerCase()));
    const filteredBhajans = selectedCategory?.bhajans?.filter(bhajan => bhajan.title.toLowerCase().includes(bhajanSearchTerm.toLowerCase()) || (bhajan.bhajan_text && bhajan.bhajan_text.toLowerCase().includes(bhajanSearchTerm.toLowerCase()))) || [];

    if (loading) return <div className="bhajan-management"><div className="loading-message">Loading bhajan categories...</div></div>;

    return (
        <div className="bhajan-management">
            {/* Edit Category Modal */}
            {editingCategory && (
                <div className="edit-modal-overlay">
                    <div className="edit-modal">
                        <h2>✏️ Edit Category</h2>
                        <div className="edit-form">
                            <label>Category Name:</label>
                            <input
                                type="text"
                                value={editCategoryName}
                                onChange={(e) => setEditCategoryName(e.target.value)}
                            />
                            <div className="edit-modal-buttons">
                                <button className="save-button" onClick={handleSaveEditCategory}>💾 Save</button>
                                <button className="cancel-button" onClick={handleCancelEditCategory}>❌ Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="management-header">
                <h2 className="section-title">Bhajan Sangrah Management</h2>
                <p className="section-description">Manage bhajan categories and their content</p>
                <div className="connection-status">
                    {error ? <span className="status-error">API Connection Issue: {error}</span> : <span className="status-success">Connected to Database</span>}
                </div>
                <button className="add-button" onClick={() => setShowAddCategoryForm(true)}>+ Add New Category</button>
            </div>

            <div className="bhajan-layout">
                <div className="categories-section">
                    <h3>Categories ({categories.length})</h3>
                    <div className="search-bar-container">
                        <input type="text" placeholder="Search categories..." value={categorySearchTerm} onChange={(e) => setCategorySearchTerm(e.target.value)} className="search-input" />
                    </div>
                    <div className="categories-grid">
                        {filteredCategories.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">🎵</div>
                                <h3>{categorySearchTerm ? 'No Matching Categories Found' : 'No Categories Found'}</h3>
                                <p>{categorySearchTerm ? 'Try adjusting your search term.' : 'Create your first bhajan category to get started.'}</p>
                            </div>
                        ) : (
                            filteredCategories.map(category => (
                                <div key={category.id} className={`category-card ${selectedCategory?.id === category.id ? 'active' : ''}`} onClick={() => fetchCategoryDetails(category.id)}>
                                    <div className="category-header">
                                        <h4>{category.name}</h4>
                                        <div className="category-actions">
                                            <button className="edit-btn" onClick={(e) => { e.stopPropagation(); handleEditCategory(category); }} title="Edit category">✏️</button>
                                            <button className="delete-btn" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id); }} title="Delete category">🗑️</button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {selectedCategory && (
                    <div className="category-details">
                        <div className="details-header">
                            <h3>{selectedCategory.name}</h3>
                            <button className="add-bhajan-btn" onClick={() => setShowAddBhajanForm(true)}>+ Add Bhajan</button>
                        </div>

                        <div className="bhajans-list">
                            <h4>Bhajans ({selectedCategory.bhajans?.length || 0})</h4>
                            <div className="search-bar-container">
                                <input type="text" placeholder="Search bhajans..." value={bhajanSearchTerm} onChange={(e) => setBhajanSearchTerm(e.target.value)} className="search-input" />
                            </div>
                            {filteredBhajans.length === 0 && selectedCategory.bhajans?.length === 0 ? (
                                <div className="empty-bhajans"><p>No bhajans in this category yet.</p></div>
                            ) : filteredBhajans.length === 0 ? (
                                <div className="empty-bhajans"><p>No matching bhajans found.</p></div>
                            ) : (
                                <div className="bhajans-grid">
                                    {filteredBhajans.map((bhajan, index) => (
                                        <BhajanCategoryCard
                                            key={index}
                                            bhajan={bhajan}
                                            categoryId={selectedCategory.id}
                                            onDelete={handleDeleteBhajan}
                                            onEdit={handleEditBhajan}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showAddCategoryForm && <AddCategoryForm onClose={() => setShowAddCategoryForm(false)} onCategoryAdded={handleCategoryAdded} />}
            {showAddBhajanForm && selectedCategory && <AddBhajanForm categoryId={selectedCategory.id} categoryName={selectedCategory.name} onClose={() => setShowAddBhajanForm(false)} onBhajanAdded={handleBhajanAdded} />}
        </div>
    );
};

export default BhajanManagement;
